import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ElementaryMissionPage from "./ElementaryMissionPage";
import LemonBotJr from "@/components/LemonBotJr";
import MissionVideo from "@/components/MissionVideo";
import MissionCompleteOverlay from "@/components/MissionCompleteOverlay";
import ChoiceCard from "@/components/ChoiceCard";
import {
  loadJrState,
  saveJrState,
  markMissionComplete,
  JR_PROBLEM_OPTIONS,
} from "@/utils/jrDemoState";
import { getMissionVideo } from "@/utils/jrMissionMedia";
import { Search } from "lucide-react";

// Mission 3's "Problem Detective" grid — only the modern (non-legacy) keys.
const M3_COLORS = {
  supplies: "yellow",
  reminders: "orange",
  encouragement: "rose",
  organize: "sky",
  "new-friends": "emerald",
  custom: "violet",
};

const M3_OPTIONS = JR_PROBLEM_OPTIONS.filter((o) => !o.legacy);

export default function Mission3() {
  const navigate = useNavigate();
  const initial = loadJrState();
  const [choice, setChoice] = useState(initial.problemChoice || "");
  const [custom, setCustom] = useState(initial.customProblem || "");
  const [showOverlay, setShowOverlay] = useState(false);
  const video = getMissionVideo(3);

  const isCustom = choice === "custom";
  const canContinue = !!choice && (!isCustom || custom.trim().length >= 3);

  const onContinue = () => {
    if (!canContinue) return;
    const opt = M3_OPTIONS.find((o) => o.key === choice);
    const businessIdea = isCustom ? custom.trim() : opt ? opt.label : "";
    saveJrState({
      problemChoice: choice,
      customProblem: isCustom ? custom.trim() : "",
      businessIdea,
    });
    markMissionComplete(3, { customized: isCustom });
    setShowOverlay(true);
  };

  return (
    <ElementaryMissionPage
      missionNumber={3}
      title="Find a Problem"
      lesson="Junior CEOs notice problems and try to help."
      canContinue={canContinue}
      onContinue={onContinue}
      continueLabel="Save & See My Plan"
      testId="elementary-mission-3-page"
    >
      {video && (
        <MissionVideo url={video.url} title={video.title} testId="mission-3-video" />
      )}

      {/* Problem Detective banner */}
      <div
        className="mb-5 bg-gradient-to-r from-sky-50 to-violet-50 ring-2 ring-sky-200 rounded-3xl p-5 flex items-center gap-4"
        data-testid="mission-3-detective-banner"
      >
        <div className="bg-sky-500 text-white w-12 h-12 rounded-2xl flex items-center justify-center shadow shrink-0">
          <Search className="w-6 h-6" />
        </div>
        <div>
          <p className="text-xs font-bold text-sky-700 uppercase tracking-wide">
            Problem Detective
          </p>
          <p className="text-gray-900 font-semibold leading-snug">
            Look for a problem people have. Helping them is your big idea!
          </p>
        </div>
      </div>

      <div className="mb-6">
        <LemonBotJr
          greeting="Junior CEOs look for problems they can help solve. Pick one that matters!"
          prompts={[
            "Pick a problem that matters to people.",
            "Your idea should make someone's day easier or better.",
          ]}
          compact
        />
      </div>

      <p className="text-lg sm:text-xl font-bold text-gray-900 mb-4">
        Which problem do you want to help solve?
      </p>

      <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
        {M3_OPTIONS.map((opt) => (
          <ChoiceCard
            key={opt.key}
            icon={opt.icon}
            label={opt.label}
            color={M3_COLORS[opt.key] || "yellow"}
            selected={choice === opt.key}
            onSelect={() => setChoice(opt.key)}
            testId={`mission-3-option-${opt.key}`}
          />
        ))}
      </div>

      {isCustom && (
        <div className="mt-5">
          <label className="block text-sm font-bold text-gray-900 mb-2">
            Tell us your idea
          </label>
          <input
            type="text"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            maxLength={120}
            placeholder="A problem I notice is…"
            className="w-full px-4 py-3 text-lg border-2 border-yellow-200 rounded-2xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
            data-testid="mission-3-custom-input"
            autoFocus
          />
        </div>
      )}

      {showOverlay && (
        <MissionCompleteOverlay
          missionNumber={3}
          missionTitle="Find a Problem"
          message="You picked a real problem to solve. Junior CEOs are made of moments like this!"
          continueLabel="See My Junior CEO Plan"
          onContinue={() => navigate("/elementary/demo")}
        />
      )}
    </ElementaryMissionPage>
  );
}
